import React, { useState } from 'react';
import Column from '@amzn/meridian/column';
import Input from '@amzn/meridian/input';
import Button from '@amzn/meridian/button';
import Alert from '@amzn/meridian/alert';
import Text from '@amzn/meridian/text';
import AuthCard from '../components/AuthCard';
import { getAuthService } from '../services';
import { isValidEmailFormat, validatePassword } from '../services/validation';

interface ForgotPasswordScreenProps {
    onBack: () => void;
}

type Step = 'request' | 'confirm' | 'done';

/**
 * Self-service password reset (code-based). Step 1 requests a code, which the
 * service emails to the account. Step 2 takes that code plus a new password.
 * Sign-in still uses email OTP MFA — this reset flow is a separate email code.
 */
export default function ForgotPasswordScreen({ onBack }: ForgotPasswordScreenProps) {
    const [step, setStep] = useState<Step>('request');
    const [email, setEmail] = useState('');
    const [code, setCode] = useState('');
    const [password, setPassword] = useState('');
    const [confirm, setConfirm] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [notice, setNotice] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState(false);

    const requestCode = async () => {
        setError(null);
        setNotice(null);
        if (!isValidEmailFormat(email)) {
            setError('Enter a valid email address.');
            return;
        }
        setSubmitting(true);
        try {
            await getAuthService().resetPassword(email.trim());
            setNotice(`If an account exists for ${email.trim()}, a reset code is on its way.`);
            setStep('confirm');
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Could not start the reset.');
        } finally {
            setSubmitting(false);
        }
    };

    const submitNewPassword = async () => {
        setError(null);
        setNotice(null);
        if (!code.trim()) {
            setError('Enter the code we emailed you.');
            return;
        }
        const pwErr = validatePassword(password);
        if (pwErr) {
            setError(pwErr);
            return;
        }
        if (password !== confirm) {
            setError('Passwords do not match.');
            return;
        }
        setSubmitting(true);
        try {
            await getAuthService().confirmResetPassword(email.trim(), code.trim(), password);
            setStep('done');
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Could not reset your password.');
        } finally {
            setSubmitting(false);
        }
    };

    if (step === 'done') {
        return (
            <AuthCard title="Password updated" subtitle="You can now sign in with your new password.">
                <Column spacing="400">
                    <Alert type="success" size="small">
                        Your password has been reset. Sign in with your new password and the
                        verification code we email you.
                    </Alert>
                    <Button type="primary" onClick={onBack}>
                        Back to sign in
                    </Button>
                </Column>
            </AuthCard>
        );
    }

    if (step === 'confirm') {
        return (
            <AuthCard title="Set a new password" subtitle={`Enter the code we emailed to ${email.trim()}.`}>
                <Column spacing="400">
                    <Input
                        id="reset-code"
                        type="text"
                        label="Verification code"
                        value={code}
                        onChange={setCode}
                    />
                    <Input
                        id="reset-password"
                        type="password"
                        label="New password"
                        value={password}
                        autoComplete="new-password"
                        onChange={setPassword}
                    />
                    <Input
                        id="reset-password-confirm"
                        type="password"
                        label="Confirm new password"
                        value={confirm}
                        autoComplete="new-password"
                        onChange={setConfirm}
                    />
                    {notice ? (
                        <Alert type="neutral" size="small">
                            {notice}
                        </Alert>
                    ) : null}
                    {error ? (
                        <Alert type="error" size="small">
                            {error}
                        </Alert>
                    ) : null}
                    <Button type="primary" disabled={submitting} onClick={submitNewPassword}>
                        {submitting ? 'Updating…' : 'Update password'}
                    </Button>
                    <Button type="link" disabled={submitting} onClick={() => setStep('request')}>
                        Use a different email
                    </Button>
                    <Button type="link" onClick={onBack}>
                        Back to sign in
                    </Button>
                </Column>
            </AuthCard>
        );
    }

    return (
        <AuthCard title="Forgot password" subtitle="We'll email you a code to reset it.">
            <Column spacing="400">
                <Input
                    id="reset-email"
                    type="email"
                    label="Email"
                    value={email}
                    autoComplete="email"
                    onChange={setEmail}
                />
                <Text type="b100" color="secondary">
                    Enter your account email. Signing in afterward still requires the emailed
                    verification code.
                </Text>
                {notice ? (
                    <Alert type="neutral" size="small">
                        {notice}
                    </Alert>
                ) : null}
                {error ? (
                    <Alert type="error" size="small">
                        {error}
                    </Alert>
                ) : null}
                <Button type="primary" disabled={submitting} onClick={requestCode}>
                    {submitting ? 'Sending…' : 'Send reset code'}
                </Button>
                <Button type="link" onClick={onBack}>
                    Back to sign in
                </Button>
            </Column>
        </AuthCard>
    );
}
